<template>
  <div class="resortPointAdd">
    <LoadingPlaceholder v-if="loading" />
    <form v-else>
      <div class="form-group">
        <h4 class="m-0 mt-3">
          Point
        </h4>
        <hr class="m-0 mb-3" />
        <label>Location</label>
        <select
          v-model="point.locationId"
          :class="[
            'form-control',
            { 'is-invalid': $validator.errors.has('Type') },
          ]"
        >
          <optgroup
            v-for="group in locationGroups"
            :key="`loc-group-${group.resortId}`"
            :label="group.label"
          >
            <option
              v-for="loc in group.locations"
              :key="`loc-point-${loc.id}`"
              :value="loc.id"
            >
              {{ `Location ${loc.id}` }}
            </option>
          </optgroup>
        </select>
        <hr class="m-0 mb-3" />
        <label>{{ $t("key") }}</label>
        <input
          v-model="point.key"
          :class="[
            'form-control',
            { 'is-invalid': $validator.errors.has('Key') },
          ]"
          type="text"
        />
        <hr class="m-0 mb-3" />
        <label>{{ $t("name") }}</label>
        <input
          v-model="point.name"
          :class="[
            'form-control',
            { 'is-invalid': $validator.errors.has('Name') },
          ]"
          type="text"
        />
        <hr class="m-0 mb-3" />
        <label>{{ $t("type") }}</label>
        <select
          v-model="point.typeId"
          :class="[
            'form-control',
            { 'is-invalid': $validator.errors.has('Type') },
          ]"
        >
          <option
            v-for="(pointType, index) in pointTypes"
            :key="`point-type-${index}`"
            :value="pointType.id"
          >
            {{ `${pointType.id} - ${pointType.name}` }}
          </option>
        </select>
        <hr class="m-0 mb-3" />
        <label>External ID</label>
        <input
          v-model="externalIdsString"
          :class="[
            'form-control',
            { 'is-invalid': $validator.errors.has('ExternalId') },
          ]"
          type="text"
        />
        <small class="form-text text-muted">You can enter multiple IDs separated by commas.</small>
        <hr class="m-0 mb-3" />
        <label>Visible</label>
        <input
          v-model="point.visible"
          type="checkbox"
          class="ml-3"
        />
        <div class="m-0 mb-3" />
        <label>{{ $t("active") }}</label>
        <input
          v-model="point.active"
          type="checkbox"
          class="ml-3"
        />
      </div>
      <LoadingPlaceholder v-if="upsertLoading" />
      <button
        v-else
        class="btn btn-primary float-right"
        :disabled="!point.name || !point.key || !point.locationId"
        @click.prevent="addPoint()"
      >
        <font-awesome-icon
          class="mr-2"
          icon="check"
        />{{ $t("add") }}
      </button>
      <div class="clearfix" />
    </form>
  </div>
</template>

<script>
import { errorMixin } from "@/mixins/errorMixin.js";

export default {
  name: "ResortPointAdd",
  mixins: [errorMixin],
  props: {
    resortId: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      pointTypes: [],
      locations: [],
      upsertLoading: false,
      loading: true,
      point: {
        key: "",
        name: "",
        typeId: null,
        locationId: null,
        externalIds: [],
        visible: true,
        active: true,
      },
    };
  },
  computed: {
    locationGroups() {
      const groups = {};
      (this.locations || []).forEach((loc) => {
        const resortId = loc.resortId || this.resortId;
        if (!groups[resortId]) {
          groups[resortId] = [];
        }
        groups[resortId].push(loc);
      });
      return Object.keys(groups)
        .map((id) => ({
          resortId: Number(id),
          label: `Resort ${id}`,
          locations: groups[id],
        }))
        .sort((a, b) => a.resortId - b.resortId);
    },
    externalIdsString: {
      get() {
        if (this.point.externalIds) {
          return this.point.externalIds.join(",");
        }
        return "";
      },
      set(val) {
        this.point.externalIds = val
          .split(",")
          .map((x) => x.trim());
      },
    },
  },
  created() {
    this.getData();
  },
  methods: {
    async getData() {
      this.loading = true;
      try {
        await Promise.all([this.getPointTypes(), this.getLocations()]);
        if (!this.point.typeId && this.pointTypes.length > 0) {
          this.point.typeId = this.pointTypes[0].id;
        }
        if (!this.point.locationId && this.locations.length > 0) {
          this.point.locationId = this.locations[0].id;
        }
      } finally {
        this.loading = false;
      }
    },
    getPointTypes() {
      return this.axios.get("/Resort/GetPointTypes")
        .then((response) => {
          this.pointTypes = response.data;
        });
    },
    getLocations() {
      return this.axios
        .get(`/Resort/GetResortLocations?resortId=${this.resortId}&includeSharedResorts=true`)
        .then((response) => {
          this.locations = response.data;
        });
    },
    async addPoint() {
      this.upsertLoading = true;
      this.point.externalIds = (this.point.externalIds || []).filter(
        (x) => x && x.length > 0
      );
      this.point.resortId = this.resortId;
      await this.axios
        .post("/Resort/CreatePointForResort", this.point)
        .then(() => {
          this.$snotify.success("Added point");
          this.$emit("reload");
          this.error_clear();
        })
        .catch((error) => {
          this.error_clear();
          this.error_validate(error);
        })
        .finally(() => {
          this.upsertLoading = false;
        });
    },
  },
};
</script>

<style scoped>
.resortPointAdd .progress {
  display: flex;
  flex: 1 1 auto;
  width: 1%;
  height: 20px;
  font-size: 1rem;
}
</style>
